import React from "react";
import Text from "@/components/ui/text";
import Badge from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import IconContainer from "@/components/ui/icon-container";
import { ChevronRight } from "@dreamhub/designsystem/icons";
import { ISOStringFormat } from "date-fns";
import { cn } from "@/utils/utils";
import { getOnboardingStageBadgeClassName } from "@/pages/settings/components/contractsSettings/onboardingStudio/colors";

interface Props {
  onboardingStartDate: ISOStringFormat | null;
  activeOnboardingStage?: string;
  activeOnboardingStageColor?: string | null;
  hasOnboardingStages: boolean;
}

// `shrink-0` so the record name beside this absorbs the header row's shrinking:
// the name truncates, and the badge keeps its own label on one line. Shared by
// all three branches below so the three cannot drift apart.
const WRAPPER_CLASS = "ml-2 shrink-0";

const OnboardingBadge: React.FC<Props> = ({
  onboardingStartDate,
  activeOnboardingStage,
  activeOnboardingStageColor,
  hasOnboardingStages
}) => {
  const { t } = useTranslation();

  const isUpcoming = onboardingStartDate
    ? new Date(onboardingStartDate) > new Date()
    : false;

  if (!onboardingStartDate || isUpcoming) {
    return (
      <div
        className={WRAPPER_CLASS}
        data-test="contract-detailed-view-onboarding-breadcrumb"
      >
        <Badge className="py-1 border-border-secondary-button">
          <Text variant="body">
            {t("contracts.detailedView.onboarding.notStarted")}
          </Text>
        </Badge>
      </div>
    );
  }

  if (!hasOnboardingStages) {
    return (
      <div
        className={WRAPPER_CLASS}
        data-test="contract-detailed-view-onboarding-breadcrumb"
      >
        <Badge selected className="py-1 border-border-secondary-button">
          <Text variant="body">
            {t("contracts.detailedView.tabs.onboarding")}
          </Text>
        </Badge>
      </div>
    );
  }

  const badgeClassName = activeOnboardingStage
    ? getOnboardingStageBadgeClassName(activeOnboardingStageColor)
    : "border-border-secondary-button";

  return (
    <div
      className={WRAPPER_CLASS}
      data-test="contract-detailed-view-onboarding-breadcrumb"
    >
      <Badge className={cn("py-1", badgeClassName)}>
        <div className="flex items-center gap-1">
          <Text variant="body">
            {t("contracts.detailedView.tabs.onboarding")}
          </Text>
          {activeOnboardingStage ? (
            <>
              <IconContainer color="secondary">
                <ChevronRight />
              </IconContainer>
              <Text variant="body">{activeOnboardingStage}</Text>
            </>
          ) : null}
        </div>
      </Badge>
    </div>
  );
};

export default OnboardingBadge;
